// ─── CHARACTERS CREATED IN THE APP ──────────────────────────────────────────
//
// Haley, 2026-08-10: "we also need the ability to add new characters to the
// character editor."
//
// THE PROBLEM. The roster is a static array in src/characters.js — 456 entries,
// imported directly by 14 files. Everything downstream (prompts, casting, the
// encyclopedia, relationship panels, scene casting) reads that array. So a
// character created at runtime had nowhere to exist: `character_overrides` is
// keyed by a code id and, measured today, all 186 of its rows have one. There
// were zero DB-only characters because there was no way to make one.
//
// THE APPROACH, and why this one. An override row whose id is NOT in the code
// array is unambiguously a new character — no schema change, no migration, no
// flag column to keep in sync with anything. Custom characters are then
// REGISTERED INTO the exported array at boot, so all 14 importers see them with
// no call-site changes at all.
//
// Mutating a shared export is not lovely, and the honest reason for it is risk:
// rewriting 14 import sites to a new merged roster is exactly the kind of broad
// mechanical change that shipped a ReferenceError to production earlier today.
// This does one thing, in one place, and every reader is already reading the
// array it appends to. If the roster is ever refactored behind an accessor,
// this becomes a line inside it.
//
// IDEMPOTENT ON PURPOSE — registration can run again after a save or a reload
// without duplicating anyone.

import { CHARACTERS } from './characters.js';
import { supabase } from './supabaseClient.js';

/** ids defined in code, captured ONCE before anything is appended. */
const CODE_IDS = new Set(CHARACTERS.map(c => c?.id).filter(Boolean));

/** Is this character defined in src/characters.js rather than the database? */
export function isCodeCharacter(id) {
  return CODE_IDS.has(id);
}

/** A safe id from a display name: lowercase, underscores, no collisions. */
export function idFromName(name, taken = CODE_IDS) {
  const base = String(name || '').toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'character';
  if (!taken.has(base)) return base;
  for (let n = 2; n < 500; n++) {
    const candidate = `${base}_${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}_${Date.now()}`;
}

/**
 * Build a character object from an override row. Only the fields the roster
 * actually needs — everything else is merged in later by
 * mergeCharWithOverrides, exactly as it is for a code character.
 */
export function characterFromRow(row) {
  if (!row?.id) return null;
  return {
    id: row.id,
    name: row.name || row.id,
    species: row.species || 'Unknown',
    generation: row.generation ?? 1,
    controlledBy: 'AI',
    color: '#b58cff',            // no color column; a stable default reads fine
    avatar: row.avatar || '🎭',
    fullName: row.full_name || row.name || row.id,
    personality: row.personality || '',
    speechPattern: row.speech_pattern || '',
    relationships: row.relationships || '',
    traits: Array.isArray(row.traits) ? row.traits : [],
    backstory: row.backstory || '',
    // Marks it as app-created, so UI can offer delete and so nothing mistakes
    // it for canon that came out of the archive.
    isCustom: true,
  };
}

/**
 * Append DB-only characters to the shared roster. Returns the ones added.
 * Safe to call repeatedly.
 */
export function registerCustomCharacters(rows) {
  const present = new Set(CHARACTERS.map(c => c?.id).filter(Boolean));
  const added = [];
  for (const row of rows || []) {
    if (!row?.id || CODE_IDS.has(row.id) || present.has(row.id)) continue;
    const char = characterFromRow(row);
    if (!char) continue;
    CHARACTERS.push(char);
    present.add(char.id);
    added.push(char);
  }
  // Mutating the array reaches every render-time reader, but MEMOIZED
  // derivations (allChars in the shell — the artery feeding Contacts, the
  // dial screen, story casting) recompute only when a dependency changes.
  // Vale (2026-08-20) registered fine and stayed invisible in exactly those
  // surfaces. The event is the roster's change signal; the shell subscribes
  // and bumps a version counter that sits in those deps.
  if (added.length > 0 && typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent('mfml-roster-changed', { detail: { added: added.map(c => c.id) } })); }
    catch { /* non-browser context (tests) */ }
  }
  return added;
}

/**
 * Load custom characters from the database and register them.
 *
 * Called at boot. A failure here must never block the app — the built-in cast
 * still works — but it must be LOUD, because a silent failure would look like
 * "my character disappeared".
 */
export async function loadAndRegisterCustomCharacters() {
  try {
    const { data, error } = await supabase
      .from('character_overrides')
      // NOTE: no `color` column exists on character_overrides — selecting it
      // would 400 the whole query and silently yield zero custom characters.
      // Verified against information_schema rather than assumed.
      .select('id, name, species, generation, avatar, full_name, personality, speech_pattern, relationships, traits, backstory');
    if (error) {
      console.error('[characters] could not load custom characters:', error.message);
      return [];
    }
    const custom = (data || []).filter(r => r?.id && !CODE_IDS.has(r.id));
    return registerCustomCharacters(custom);
  } catch (e) {
    console.error('[characters] custom character load threw:', e);
    return [];
  }
}

/**
 * Re-run the loader once a session exists.
 *
 * The boot call in main.jsx fires at module load — BEFORE supabase-js has
 * restored the session from storage. character_overrides' SELECT policy is
 * granted to `authenticated` only, so that pre-auth query runs as `anon` and
 * RLS hands back zero rows: no error, no console noise, just an empty custom
 * cast. That is the exact shape of "I added a character and he isn't
 * anywhere" (Vale, 2026-08-20). Re-running on the auth events closes it, and
 * registration is idempotent so the double-run costs nothing.
 */
export function armCustomCharacterLoaderOnAuth() {
  try {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')) {
        loadAndRegisterCustomCharacters();
      }
    });
    return data?.subscription;
  } catch (e) {
    console.error('[characters] auth-armed loader failed to arm:', e);
    return null;
  }
}
